import * as THREE from "three";
import { CHUNK_SIZE_UNITS, type TierDefinition } from "../core/config";
import { terrainHeightMeters } from "../core/TokyoTerrain";
import { TokyoScenery } from "./TokyoScenery";

const GROUND_RENDER_DIAMETER_UNITS = 220;
const GROUND_SEGMENTS = 36;

export class Environment {
  readonly group = new THREE.Group();
  readonly scenery = new TokyoScenery();

  private readonly groundMaterial: THREE.ShaderMaterial;
  private readonly skyMaterial: THREE.ShaderMaterial;
  private readonly groundGeometry: THREE.PlaneGeometry;
  private readonly ground: THREE.Mesh;
  private readonly sky: THREE.Mesh;
  private readonly normalizedGroundXZ: Float32Array;
  private readonly groundColor = new THREE.Color();
  private readonly accentColor = new THREE.Color();
  private readonly skyColor = new THREE.Color();
  private readonly horizonColor = new THREE.Color();
  private readonly targetColor = new THREE.Color();
  private readonly daylightSky = new THREE.Color(0x70c9f4);
  private readonly daylightHorizon = new THREE.Color(0xe8f7ff);
  private readonly daylightGround = new THREE.Color(0x84ad78);

  private groundTierIndex = -1;
  private groundAnchorXMeters = Number.NaN;
  private groundAnchorZMeters = Number.NaN;

  constructor(private readonly scene: THREE.Scene) {
    this.group.name = "procedural-tokyo-environment";
    this.groundMaterial = this.createGroundMaterial();
    this.skyMaterial = this.createSkyMaterial();

    this.groundGeometry = new THREE.PlaneGeometry(
      1,
      1,
      GROUND_SEGMENTS,
      GROUND_SEGMENTS,
    );
    this.groundGeometry.rotateX(-Math.PI * 0.5);
    this.groundGeometry.name = "bounded-tokyo-heightfield-geometry";
    const groundPosition = this.groundGeometry.getAttribute("position");
    this.normalizedGroundXZ = new Float32Array(groundPosition.count * 2);
    for (let index = 0; index < groundPosition.count; index += 1) {
      this.normalizedGroundXZ[index * 2] = groundPosition.getX(index);
      this.normalizedGroundXZ[index * 2 + 1] = groundPosition.getZ(index);
    }

    this.ground = new THREE.Mesh(this.groundGeometry, this.groundMaterial);
    // Preserve the original lookup name used by diagnostics while replacing
    // its flat geometry with the deterministic Tokyo heightfield.
    this.ground.name = "infinite-grid-ground";
    this.ground.frustumCulled = false;

    const skyGeometry = new THREE.SphereGeometry(95, 24, 14);
    this.sky = new THREE.Mesh(skyGeometry, this.skyMaterial);
    this.sky.name = "tokyo-daylight-sky";
    this.sky.frustumCulled = false;

    this.group.add(this.ground, this.sky, this.scenery.group);
  }

  update(
    playerXMeters: number,
    playerZMeters: number,
    renderMetersPerUnit: number,
    elapsedSeconds: number,
    fromTier: TierDefinition,
    toTier: TierDefinition,
    blend: number,
  ): void {
    const boundedBlend = THREE.MathUtils.clamp(blend, 0, 1);
    const visualTier = boundedBlend < 0.5 ? fromTier : toTier;
    const safeRenderScale = Math.max(0.000_001, renderMetersPerUnit);
    const [anchorX, anchorZ] = this.anchorFor(
      playerXMeters,
      playerZMeters,
      visualTier,
    );
    if (
      visualTier.id !== this.groundTierIndex
      || anchorX !== this.groundAnchorXMeters
      || anchorZ !== this.groundAnchorZMeters
    ) {
      this.rebuildGround(visualTier, anchorX, anchorZ);
    }

    const playerGround = terrainHeightMeters(
      playerXMeters,
      playerZMeters,
      visualTier.id,
    );
    this.ground.scale.setScalar(1 / safeRenderScale);
    this.ground.position.set(
      (this.groundAnchorXMeters - playerXMeters) / safeRenderScale,
      -playerGround / safeRenderScale,
      (this.groundAnchorZMeters - playerZMeters) / safeRenderScale,
    );
    this.scenery.update(
      playerXMeters,
      playerZMeters,
      safeRenderScale,
      visualTier.id,
    );

    this.groundColor
      .set(fromTier.ground)
      .lerp(this.targetColor.set(toTier.ground), boundedBlend)
      .lerp(this.daylightGround, 0.48);
    this.accentColor
      .set(fromTier.accent)
      .lerp(this.targetColor.set(toTier.accent), boundedBlend)
      .lerp(this.daylightHorizon, 0.1);
    this.skyColor
      .set(fromTier.sky)
      .lerp(this.targetColor.set(toTier.sky), boundedBlend)
      .lerp(this.daylightSky, 0.72);
    this.horizonColor
      .set(fromTier.horizon)
      .lerp(this.targetColor.set(toTier.horizon), boundedBlend)
      .lerp(this.daylightHorizon, 0.68);

    this.groundMaterial.uniforms["uTime"]!.value = elapsedSeconds;
    this.groundMaterial.uniforms["uGround"]!.value.copy(this.groundColor);
    this.groundMaterial.uniforms["uAccent"]!.value.copy(this.accentColor);
    this.groundMaterial.uniforms["uGridMeters"]!.value = visualTier.metersPerUnit * 2;
    this.skyMaterial.uniforms["uSky"]!.value.copy(this.skyColor);
    this.skyMaterial.uniforms["uHorizon"]!.value.copy(this.horizonColor);
    this.skyMaterial.uniforms["uAccent"]!.value.copy(this.accentColor);

    const fogDensity = THREE.MathUtils.lerp(
      fromTier.fogDensity,
      toTier.fogDensity,
      boundedBlend,
    ) * 0.68;
    if (this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.color.copy(this.horizonColor);
      this.scene.fog.density = fogDensity;
    } else {
      this.scene.fog = new THREE.FogExp2(this.horizonColor, fogDensity);
    }
    this.scene.background = this.skyColor;
  }

  dispose(): void {
    this.groundGeometry.dispose();
    this.groundMaterial.dispose();
    this.sky.geometry.dispose();
    this.skyMaterial.dispose();
    this.scenery.dispose();
    this.group.clear();
  }

  private anchorFor(
    playerXMeters: number,
    playerZMeters: number,
    tier: TierDefinition,
  ): readonly [number, number] {
    const cellSize = tier.metersPerUnit * CHUNK_SIZE_UNITS;
    return [
      Math.round(playerXMeters / cellSize) * cellSize,
      Math.round(playerZMeters / cellSize) * cellSize,
    ];
  }

  private rebuildGround(
    tier: TierDefinition,
    anchorXMeters: number,
    anchorZMeters: number,
  ): void {
    this.groundTierIndex = tier.id;
    this.groundAnchorXMeters = anchorXMeters;
    this.groundAnchorZMeters = anchorZMeters;
    const widthMeters = tier.metersPerUnit * GROUND_RENDER_DIAMETER_UNITS;
    const positions = this.groundGeometry.getAttribute("position") as THREE.BufferAttribute;
    for (let index = 0; index < positions.count; index += 1) {
      const xMeters = (this.normalizedGroundXZ[index * 2] ?? 0) * widthMeters;
      const zMeters = (this.normalizedGroundXZ[index * 2 + 1] ?? 0) * widthMeters;
      const heightMeters = terrainHeightMeters(
        anchorXMeters + xMeters,
        anchorZMeters + zMeters,
        tier.id,
      );
      positions.setXYZ(index, xMeters, heightMeters, zMeters);
    }
    positions.needsUpdate = true;
    this.groundGeometry.computeVertexNormals();
    this.groundGeometry.computeBoundingBox();
    this.groundGeometry.computeBoundingSphere();
    this.groundMaterial.uniforms["uAnchor"]!.value.set(anchorXMeters, anchorZMeters);
  }

  private createGroundMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.merge([
        THREE.UniformsLib.fog,
        {
          uAnchor: { value: new THREE.Vector2() },
          uGridMeters: { value: 0.5 },
          uTime: { value: 0 },
          uGround: { value: new THREE.Color(0x78a873) },
          uAccent: { value: new THREE.Color(0xffd76f) },
        },
      ]),
      vertexShader: `
        #include <fog_pars_vertex>
        uniform vec2 uAnchor;
        varying vec2 vWorldMeters;
        varying float vHeight;
        void main() {
          vWorldMeters = position.xz + uAnchor;
          vHeight = position.y;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          #include <fog_vertex>
        }
      `,
      fragmentShader: `
        #include <fog_pars_fragment>
        uniform float uGridMeters;
        uniform float uTime;
        uniform vec3 uGround;
        uniform vec3 uAccent;
        varying vec2 vWorldMeters;
        varying float vHeight;

        float gridLine(vec2 coordinate, float spacing, float width) {
          vec2 derivative = max(fwidth(coordinate / spacing), vec2(0.00001));
          vec2 cell = abs(fract(coordinate / spacing - 0.5) - 0.5) / derivative;
          return 1.0 - min(min(cell.x, cell.y) / width, 1.0);
        }

        void main() {
          float small = gridLine(vWorldMeters, uGridMeters, 1.0) * 0.055;
          float blocks = gridLine(vWorldMeters, uGridMeters * 6.0, 1.15) * 0.11;
          float sunWash = 0.985 + 0.015 * sin(uTime * 0.12 + vHeight * 0.04);
          vec3 base = uGround * sunWash + vec3(0.055, 0.065, 0.045);
          vec3 color = mix(base, uAccent, small + blocks);
          gl_FragColor = vec4(color, 1.0);
          #include <fog_fragment>
        }
      `,
      fog: true,
    });
  }

  private createSkyMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        uSky: { value: new THREE.Color(0x70c9f4) },
        uHorizon: { value: new THREE.Color(0xe8f7ff) },
        uAccent: { value: new THREE.Color(0xffd76f) },
      },
      vertexShader: `
        varying vec3 vWorld;
        void main() {
          vWorld = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uSky;
        uniform vec3 uHorizon;
        uniform vec3 uAccent;
        varying vec3 vWorld;
        void main() {
          float height = smoothstep(-0.22, 0.76, vWorld.y);
          float horizonMist = exp(-abs(vWorld.y) * 7.5) * 0.16;
          vec3 sunDirection = normalize(vec3(-0.42, 0.58, -0.70));
          float sun = pow(max(dot(normalize(vWorld), sunDirection), 0.0), 420.0);
          float halo = pow(max(dot(normalize(vWorld), sunDirection), 0.0), 28.0);
          vec3 color = mix(uHorizon, uSky, height);
          color += uHorizon * horizonMist + uAccent * halo * 0.15 + vec3(1.0, 0.94, 0.74) * sun;
          gl_FragColor = vec4(color, 1.0);
        }
      `,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
  }
}
